import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Loader2, Lock, Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { CatalogUnit } from "@/lib/types";
import type { CatalogUnitInput, CatalogUnitUsage } from "@/lib/catalogApi";

/** Grupos oferecidos no cadastro. "OUTRAS" é a ausência de grupo (enviada como null ao backend). */
export const UNIT_GROUP_OPTIONS = ["LITROS", "BANDEJA", "KG", "PACOTE", "UNIDADE", "OUTRAS"] as const;
const OUTRAS = "OUTRAS";

export interface UnitDialogProps {
  open: boolean;
  mode: "create" | "edit";
  units: CatalogUnit[];
  initialUnitId?: string | null;
  onClose: () => void;
  onCreate: (input: CatalogUnitInput) => Promise<CatalogUnit>;
  onUpdate: (id: string, input: CatalogUnitInput) => Promise<CatalogUnit>;
  onFetchUsage: (id: string) => Promise<CatalogUnitUsage>;
}

export function UnitDialog({
  open,
  mode,
  units,
  initialUnitId,
  onClose,
  onCreate,
  onUpdate,
  onFetchUsage,
}: UnitDialogProps) {
  const [selectedId, setSelectedId] = useState<string>("");
  const [name, setName] = useState("");
  const [group, setGroup] = useState<string>(OUTRAS);
  const [conversion, setConversion] = useState("");
  const [active, setActive] = useState(true);
  const [saving, setSaving] = useState(false);
  // Fluxo de confirmação: alteração de conversionToKg exige confirmar o impacto global antes de salvar.
  const [confirming, setConfirming] = useState(false);
  const [usage, setUsage] = useState<CatalogUnitUsage | null>(null);
  const [usageLoading, setUsageLoading] = useState(false);

  const editableUnits = useMemo(() => [...units].sort((a, b) => a.name.localeCompare(b.name)), [units]);
  const selectedUnit = useMemo(
    () => editableUnits.find((unit) => unit.id === selectedId) ?? null,
    [editableUnits, selectedId],
  );
  // Unidade em uso por algum produto: nome não pode ser renomeado (vínculo produto→unidade é por nome).
  const inUse = mode === "edit" && (usage?.productCount ?? 0) > 0;
  const nameChanged = !!selectedUnit && name.trim() !== selectedUnit.name;

  const populateFrom = (unit: CatalogUnit) => {
    setName(unit.name);
    setGroup(unit.group && UNIT_GROUP_OPTIONS.includes(unit.group as (typeof UNIT_GROUP_OPTIONS)[number]) ? unit.group : OUTRAS);
    setConversion(String(unit.conversionToKg));
    setActive(unit.active);
  };

  const loadUsage = async (id: string) => {
    setUsageLoading(true);
    try {
      setUsage(await onFetchUsage(id));
    } catch {
      // Falha ao consultar uso não bloqueia conversão/status/grupo; o backend ainda barra rename em uso.
      setUsage(null);
    } finally {
      setUsageLoading(false);
    }
  };

  const resetForm = () => {
    setName("");
    setGroup(OUTRAS);
    setConversion("");
    setActive(true);
    setConfirming(false);
    setUsage(null);
    setUsageLoading(false);
  };

  useEffect(() => {
    if (!open) return;
    resetForm();
    if (mode === "edit") {
      const preselect = initialUnitId ?? "";
      setSelectedId(preselect);
      const unit = units.find((item) => item.id === preselect);
      if (unit) {
        populateFrom(unit);
        void loadUsage(unit.id);
      }
    } else {
      setSelectedId("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, initialUnitId]);

  const onSelectUnit = (id: string) => {
    setSelectedId(id);
    setConfirming(false);
    setUsage(null);
    const unit = units.find((item) => item.id === id);
    if (unit) {
      populateFrom(unit);
      void loadUsage(id);
    }
  };

  const buildInput = (): CatalogUnitInput | null => {
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error("Informe o nome da unidade");
      return null;
    }
    const parsed = parseFloat(conversion.replace(",", "."));
    if (!Number.isFinite(parsed) || parsed <= 0) {
      toast.error("A conversão para kg deve ser maior que zero");
      return null;
    }
    return {
      name: trimmed,
      group: group === OUTRAS ? null : group,
      conversionToKg: parsed,
      active,
    };
  };

  const conversionChanged = () => {
    if (!selectedUnit) return false;
    const parsed = parseFloat(conversion.replace(",", "."));
    return Number.isFinite(parsed) && parsed !== selectedUnit.conversionToKg;
  };

  const persist = async (input: CatalogUnitInput) => {
    setSaving(true);
    try {
      if (mode === "edit" && selectedUnit) {
        await onUpdate(selectedUnit.id, input);
        toast.success("Unidade atualizada");
      } else {
        await onCreate(input);
        toast.success("Unidade criada");
      }
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Não foi possível salvar a unidade");
    } finally {
      setSaving(false);
    }
  };

  const submit = async () => {
    const input = buildInput();
    if (!input) return;

    if (mode === "edit") {
      if (!selectedUnit) {
        toast.error("Selecione a unidade que deseja editar");
        return;
      }
      // Guarda de UX: unidade em uso não pode ser renomeada (o backend também barra).
      if (nameChanged && inUse) {
        toast.error("Esta unidade está em uso por produtos e não pode ser renomeada. Altere conversão, status ou grupo.");
        return;
      }
      // Só altera conversão com confirmação explícita do impacto global.
      if (conversionChanged() && !confirming) {
        if (!usage) await loadUsage(selectedUnit.id);
        setConfirming(true);
        return;
      }
    }

    await persist(input);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-md overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {mode === "edit" ? "Editar unidade" : "Adicionar unidade"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          {mode === "edit" && (
            <div className="space-y-2">
              <Label>Unidade *</Label>
              <Select value={selectedId} onValueChange={onSelectUnit}>
                <SelectTrigger><SelectValue placeholder="Selecione a unidade" /></SelectTrigger>
                <SelectContent>
                  {editableUnits.map((unit) => (
                    <SelectItem key={unit.id} value={unit.id}>
                      {unit.name}{unit.active ? "" : " (inativa)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {(mode === "create" || selectedUnit) && (
            <>
              <div className="space-y-2">
                <Label>Nome *</Label>
                <Input
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="Ex.: Bandeja 500g"
                  readOnly={inUse}
                  className={inUse ? "cursor-not-allowed opacity-70" : undefined}
                />
                {inUse && (
                  <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Lock className="h-3.5 w-3.5" />
                    Unidade em uso por {usage?.productCount}{" "}
                    {usage?.productCount === 1 ? "produto" : "produtos"} — o nome não pode ser alterado.
                    Conversão, status e grupo continuam editáveis.
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Grupo</Label>
                  <Select value={group} onValueChange={setGroup}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {UNIT_GROUP_OPTIONS.map((option) => (
                        <SelectItem key={option} value={option}>{option}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={active ? "active" : "inactive"} onValueChange={(value) => setActive(value === "active")}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Ativa</SelectItem>
                      <SelectItem value="inactive">Inativa</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Conversão para kg *</Label>
                <Input
                  type="number"
                  min={0}
                  step="any"
                  value={conversion}
                  onChange={(event) => {
                    setConversion(event.target.value);
                    // Editar o valor de novo invalida uma confirmação anterior.
                    setConfirming(false);
                  }}
                  className="tabular-nums"
                  placeholder="Ex.: 0.5"
                />
                <p className="text-xs text-muted-foreground">
                  Quantos kg equivalem a 1 unidade. Afeta o preço por peso em todo o catálogo.
                </p>
              </div>

              {confirming && (
                <div className="space-y-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-amber-900">
                  <div className="flex items-center gap-2 font-medium">
                    <AlertTriangle className="h-4 w-4" />
                    Alteração de conversão com impacto global
                  </div>
                  <p className="text-xs">
                    Mudar a conversão desta unidade recalcula o preço por peso de{" "}
                    <strong>{usage?.productCount ?? 0}</strong>{" "}
                    {usage?.productCount === 1 ? "produto que a utiliza" : "produtos que a utilizam"} em todo o catálogo.
                  </p>
                  {usage && usage.products.length > 0 && (
                    <ul className="max-h-32 space-y-0.5 overflow-y-auto text-xs">
                      {usage.products.map((product) => (
                        <li key={product.id}>
                          • {product.code != null ? `#${product.code} ` : ""}{product.name}
                        </li>
                      ))}
                    </ul>
                  )}
                  <p className="text-xs font-medium">Confirme para aplicar a alteração.</p>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button
            variant={confirming ? "destructive" : "hero"}
            onClick={() => void submit()}
            disabled={saving || usageLoading || (mode === "edit" && !selectedUnit)}
          >
            {(saving || usageLoading) && <Loader2 className="h-4 w-4 animate-spin" />}
            {confirming
              ? "Confirmar alteração"
              : mode === "edit"
                ? <><Pencil className="h-4 w-4" /> Salvar unidade</>
                : <><Plus className="h-4 w-4" /> Criar unidade</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
